/**
 * Step structure constants for PDM-sourced steps.
 * Structure (keys/titles/order) lives in code; option values come from PDM.
 * Presentation assets (images) may still come from Dato.
 */

import type { Step, Substep } from "@views/Upholstery/types"

/**
 * Body step structure — option values filled from PDM
 */
export const BODY_STEP_STRUCTURE: Omit<Step, "category"> = {
  id: "pdm-body-step",
  key: "body",
  name: "Configuration",
  title: "Configuration",
  description: "Select your vehicle's body configuration",
  substeps: [
    {
      id: "pdm-body-trim",
      key: "trim",
      name: "Trim",
      title: "Trim",
      description: "Vehicle trim level",
      category: {
        optionName: "Trim",
        optionValues: [], // Filled by PDM
      },
    },
    {
      id: "pdm-body-body-step",
      key: "body-step",
      name: "Body Type",
      title: "Body Type",
      description: "Vehicle body configuration",
      category: {
        optionName: "Body Type",
        optionValues: [], // Filled by PDM
      },
    },
    {
      id: "pdm-body-driver-seat-type",
      key: "driver-seat-type",
      name: "Driver Seat Type",
      title: "Driver Seat Type",
      description: "Factory driver seat type",
      category: {
        optionName: "Driver Seat Type",
        optionValues: [], // Filled by PDM
      },
    },
    {
      id: "pdm-body-passenger-seat-type",
      key: "passenger-seat-type",
      name: "Passenger Seat Type",
      title: "Passenger Seat Type",
      description: "Factory passenger seat type",
      category: {
        optionName: "Passenger Seat Type",
        optionValues: [], // Filled by PDM
      },
    },
  ] as Substep[],
  category: {
    optionName: "Body Configuration",
    optionValues: [],
  },
  factoryMatches: null,
}

/**
 * Rows step structure — option labels from PDM; images/SKUs may overlay from Dato
 */
export const ROWS_STEP_STRUCTURE: Omit<Step, "category"> = {
  id: "pdm-rows-step",
  key: "rows",
  name: "Rows",
  title: "Rows",
  description: "Select configuration for each row",
  substeps: [
    {
      id: "pdm-rows-front",
      key: "front",
      name: "Front Row",
      title: "Front Row",
      description: "Front row seating configuration",
      category: {
        optionName: "Front Row",
        optionValues: [], // Filled by PDM
      },
    },
    {
      id: "pdm-rows-back",
      key: "back",
      name: "Back Row",
      title: "Back Row",
      description: "Back row seating configuration",
      category: {
        optionName: "Back Row",
        optionValues: [], // Filled by PDM
      },
    },
    {
      id: "pdm-rows-middle",
      key: "middle",
      name: "Middle Row",
      title: "Middle Row",
      description: "Middle row seating configuration",
      category: {
        optionName: "Middle Row",
        optionValues: [], // Filled by PDM
      },
    },
  ] as Substep[],
  category: {
    optionName: "Row Configuration",
    optionValues: [],
  },
  factoryMatches: null,
}

/**
 * Factory Match step structure — cards from PDM; tile images may use Dato assets
 */
export const FACTORY_MATCH_STEP_STRUCTURE: Omit<Step, "category"> = {
  id: "pdm-factory-match-step",
  key: "factory-match",
  name: "Factory Match",
  title: "Factory Match",
  description: "Select factory matched part",
  substeps: [],
  category: {
    optionName: "Factory Match",
    optionValues: [],
  },
  factoryMatches: [], // Filled by PDM
}

/**
 * All PDM-sourced steps in the order they should appear
 */
export const PDM_STEPS_ORDER = ["body", "rows", "factory-match"] as const

export type PdmStepKey = (typeof PDM_STEPS_ORDER)[number]

/**
 * Get a fresh copy of the step structure for a given PDM step key
 */
export function getPdmStepStructure(key: PdmStepKey): Step {
  switch (key) {
    case "body":
      return JSON.parse(JSON.stringify(BODY_STEP_STRUCTURE)) as Step
    case "rows":
      return JSON.parse(JSON.stringify(ROWS_STEP_STRUCTURE)) as Step
    case "factory-match":
      return JSON.parse(JSON.stringify(FACTORY_MATCH_STEP_STRUCTURE)) as Step
  }
}

/**
 * Check if a step key is PDM-sourced (should not use Dato template)
 */
export function isPdmSourcedStep(stepKey: string): stepKey is PdmStepKey {
  return PDM_STEPS_ORDER.includes(stepKey as PdmStepKey)
}
